import { useEffect, useState } from "react";

import CrudModal from "components/crud/CrudModal";
import CrudInput from "components/crud/CrudInput";
import CrudSelect from "components/crud/CrudSelect";

import LocationCascade from "components/location/LocationCascade";

import {
  createParty,
  updateParty,
} from "services/api/partyApi";

import {
  getEmployees,
} from "services/api/employeeApi";

import {
  showSuccess,
  showError,
} from "utils/toast";


const INITIAL_FORM = {

  partyCode: "",

  name: "",

  type: "bookseller",

  phone: "",

  email: "",

  gstNumber: "",

  addressLine1: "",

  addressLine2: "",

  pincode: "",

  stateId: "",

  districtId: "",

  cityId: "",

  zoneId: "",

  areaId: "",

  beatId: "",

  territoryId: "",

  assignedEmployeeId: "",

  openingBalance: "",

  creditLimit: "",

  remarks: "",

  status: "active",

};


const EMPTY_CONTACT = {

  name: "",

  designation: "",

  phone: "",

  email: "",

  isPrimary: false,

};


export default function PartyModal({
  open,
  onClose,
  reload,
  item,
}) {

  const isEdit =
    Boolean(item);


  const [loading, setLoading] =
    useState(false);


  const [employees, setEmployees] =
    useState([]);


  const [contacts, setContacts] =
    useState([]);


  const [form, setForm] =
    useState(INITIAL_FORM);


  const [currentUser, setCurrentUser] =
    useState({});


  /*
  |--------------------------------------------------------------------------
  | ROLE
  |--------------------------------------------------------------------------
  */

  const roles =
    Array.isArray(
      currentUser?.roles
    )
      ? currentUser.roles
      : [];


  const isAdmin =
    roles.includes("admin") ||
    roles.includes("super_admin");


  const isManager =
    roles.includes("manager") ||
    roles.includes("team_leader") ||
    roles.includes("regional_manager");


  const isSalesPerson =
    roles.includes("sales_person");


  /*
  |--------------------------------------------------------------------------
  | LOAD USER
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    try {

      const storedUser =
        localStorage.getItem(
          "user"
        );

      if (storedUser) {

        setCurrentUser(
          JSON.parse(
            storedUser
          ) || {}
        );

      }

    } catch (error) {

      console.error(
        "Unable to parse user:",
        error
      );

      setCurrentUser({});

    }

  }, []);


  /*
  |--------------------------------------------------------------------------
  | LOAD EMPLOYEES
  |--------------------------------------------------------------------------
  */

  const loadEmployees =
    async () => {

      try {

        const response =
          await getEmployees(
            1,
            500
          );


        const data =
          response?.data?.data
            ?.employees ||
          response?.data?.employees ||
          response?.data?.data
            ?.records ||
          response?.data?.records ||
          [];


        setEmployees(
          Array.isArray(data)
            ? data
            : []
        );

      } catch (error) {

        console.error(
          "Employee loading error:",
          error
        );

        setEmployees([]);

      }

    };


  /*
  |--------------------------------------------------------------------------
  | INITIALIZE MODAL
  |--------------------------------------------------------------------------
  */

  useEffect(() => {

    if (!open) {
      return;
    }


    loadEmployees();


    if (item) {

      setForm({

        partyCode:
          item.partyCode || "",

        name:
          item.name || "",

        type:
          item.type || "bookseller",

        phone:
          item.phone || "",

        email:
          item.email || "",

        gstNumber:
          item.gstNumber || "",

        addressLine1:
          item.addressLine1 || "",

        addressLine2:
          item.addressLine2 || "",

        pincode:
          item.pincode || "",

        stateId:
          item.location?.state?.id ||
          "",

        districtId:
          item.location?.district?.id ||
          "",

        cityId:
          item.location?.city?.id ||
          "",

        zoneId:
          item.location?.zone?.id ||
          "",

        areaId:
          item.location?.area?.id ||
          "",

        beatId:
          item.location?.beat?.id ||
          "",

        territoryId:
          item.location?.territory?.id ||
          "",

        assignedEmployeeId:
          item.assignedEmployee?.id ||
          "",

        openingBalance:
          item.openingBalance || "",

        creditLimit:
          item.creditLimit || "",

        remarks:
          item.remarks || "",

        status:
          item.status || "active",

      });


      setContacts(
        item.contacts || []
      );

      return;
    }


    /*
    CREATE
    */

    const loggedInEmployeeId =
      currentUser?.employee?.id ||
      "";


    setForm({

      ...INITIAL_FORM,

      /*
      Sales person automatically
      gets himself assigned.
      */

      assignedEmployeeId:
        isSalesPerson
          ? String(
              loggedInEmployeeId
            )
          : "",

    });


    setContacts([]);

  }, [
    open,
    item,
    isSalesPerson,
    currentUser,
  ]);


  /*
  |--------------------------------------------------------------------------
  | INPUT
  |--------------------------------------------------------------------------
  */

  const handleChange =
    (e) => {

      const {
        name,
        value,
      } = e.target;


      setForm(
        prev => ({
          ...prev,
          [name]: value,
        })
      );

    };


  /*
  |--------------------------------------------------------------------------
  | CONTACT
  |--------------------------------------------------------------------------
  */

  const updateContact =
    (
      index,
      key,
      value
    ) => {

      setContacts(
        prev =>
          prev.map(
            (
              contact,
              i
            ) =>
              i === index
                ? {
                    ...contact,
                    [key]:
                      value,
                  }
                : contact
          )
      );

    };


  const removeContact =
    (index) => {

      setContacts(
        prev =>
          prev.filter(
            (_, i) =>
              i !== index
          )
      );

    };


  /*
  |--------------------------------------------------------------------------
  | SUBMIT
  |--------------------------------------------------------------------------
  */

  const handleSubmit =
    async (e) => {

      e.preventDefault();


      if (!form.name.trim()) {

        showError(
          "Party name is required."
        );

        return;

      }


      try {

        setLoading(true);


        const payload = {

          ...form,

          /*
          Salesperson is always
          assigned to himself.
          */

          assignedEmployeeId:
            isSalesPerson
              ? String(
                  currentUser?.employee?.id ||
                  form.assignedEmployeeId
                )
              : form.assignedEmployeeId ||
                undefined,

          openingBalance:
            form.openingBalance ||
            undefined,

          creditLimit:
            form.creditLimit ||
            undefined,

            contacts:
    contacts
        .filter(
            contact =>
                contact.name?.trim()
        )
        .map(
            contact => ({
                name:
                    contact.name,

                designation:
                    contact.designation ||
                    undefined,

                phone:
                    contact.phone ||
                    undefined,

                email:
                    contact.email ||
                    undefined,

                isPrimary:
                    Boolean(
                        contact.isPrimary
                    ),
            })
        ),

        };


        console.log(
          "PARTY PAYLOAD:",
          payload
        );


        if (isEdit) {

          await updateParty(
            item.id,
            payload
          );


          showSuccess(
            "Party updated successfully."
          );

        } else {

          await createParty(
            payload
          );


          showSuccess(
            "Party created successfully."
          );

        }


        await reload();

        onClose();

      } catch (error) {

        console.error(
          "Party save error:",
          error
        );


        showError(
          error?.response?.data?.message ||
          "Unable to save party."
        );

      } finally {

        setLoading(false);

      }

    };


  if (!open) {
    return null;
  }


  return (

    <CrudModal

      open={open}

      title={
        isEdit
          ? "Edit Party"
          : "Create Party"
      }

      loading={loading}

      submitText={
        isEdit
          ? "Update"
          : "Save"
      }

      onClose={onClose}

      onSubmit={handleSubmit}

      width="max-w-6xl"

    >

      {/* =========================================================
          BASIC
      ========================================================== */}

      <CrudInput
        label="Party Code"
        name="partyCode"
        value={form.partyCode}
        onChange={handleChange}
      />


      <CrudInput
        label="Party Name"
        name="name"
        value={form.name}
        onChange={handleChange}
      />


      <CrudSelect
        label="Type"
        value={form.type}
        onChange={(e) =>
          setForm(
            prev => ({
              ...prev,
              type: e.target.value,
            })
          )
        }
        options={[
          {
            value: "bookseller",
            label: "Bookseller",
          },
          {
            value: "distributor",
            label: "Distributor",
          },
          {
            value: "dealer",
            label: "Dealer",
          },
          {
            value: "party",
            label: "Party",
          },
        ]}
      />


      <CrudInput
        label="Phone"
        name="phone"
        value={form.phone}
        onChange={handleChange}
      />


      <CrudInput
        label="Email"
        name="email"
        value={form.email}
        onChange={handleChange}
      />


      <CrudInput
        label="GST Number"
        name="gstNumber"
        value={form.gstNumber}
        onChange={handleChange}
      />


      <CrudInput
        label="Address"
        name="addressLine1"
        value={form.addressLine1}
        onChange={handleChange}
      />


      <CrudInput
        label="Address Line 2"
        name="addressLine2"
        value={form.addressLine2}
        onChange={handleChange}
      />


      {/* =========================================================
          LOCATION
      ========================================================== */}

      <LocationCascade
        form={form}
        setForm={setForm}
      />


      <CrudInput
        label="Pincode"
        name="pincode"
        value={form.pincode}
        onChange={handleChange}
      />


      {/* =========================================================
          ASSIGN EMPLOYEE
      ========================================================== */}

      {!isSalesPerson && (

        <CrudSelect
          label="Assign Employee"
          value={
            form.assignedEmployeeId
          }
          onChange={(e) =>
            setForm(
              prev => ({
                ...prev,

                assignedEmployeeId:
                  e.target.value,
              })
            )
          }
          options={[
            {
              value: "",
              label: "Select Employee",
            },

            ...employees.map(
              (emp) => ({

                value:
                  String(emp.id),

                label:
                  emp.user?.name ||
                  emp.name ||
                  emp.employeeCode,

              })
            ),
          ]}
        />

      )}


      {isSalesPerson && (

        <div
          className="
            rounded-xl
            border
            border-blue-100
            bg-blue-50
            p-4
            text-sm
            text-blue-800
          "
        >

          This party will be assigned
          automatically to{" "}

          <strong>
            {currentUser?.name || "you"}
          </strong>.

        </div>

      )}


      {/* =========================================================
          FINANCIAL
      ========================================================== */}

      <CrudInput
        label="Opening Balance"
        name="openingBalance"
        value={form.openingBalance}
        onChange={handleChange}
      />


      <CrudInput
        label="Credit Limit"
        name="creditLimit"
        value={form.creditLimit}
        onChange={handleChange}
      />


      <CrudInput
        label="Remarks"
        name="remarks"
        value={form.remarks}
        onChange={handleChange}
      />


      {/* =========================================================
          CONTACTS
      ========================================================== */}

      <h3 className="mb-3 mt-6 text-lg font-bold text-navy-700">
        Contacts
      </h3>


      <button
        type="button"
        className="
          mb-4
          rounded-lg
          bg-brand-500
          px-4
          py-2
          font-semibold
          text-white
          hover:bg-brand-600
        "
        onClick={() =>
          setContacts(
            prev => [
              ...prev,
              {
                ...EMPTY_CONTACT,
              },
            ]
          )
        }
      >
        + Add Contact
      </button>


      {contacts.map(
        (
          contact,
          index
        ) => (

          <div
            key={
              contact.id ||
              index
            }
            className="
              mb-4
              rounded-xl
              border
              border-gray-200
              p-4
            "
          >

            <CrudInput
              label="Name"
              value={
                contact.name
              }
              onChange={(e) =>
                updateContact(
                  index,
                  "name",
                  e.target.value
                )
              }
            />


            <CrudInput
              label="Designation"
              value={
                contact.designation ||
                ""
              }
              onChange={(e) =>
                updateContact(
                  index,
                  "designation",
                  e.target.value
                )
              }
            />


            <CrudInput
              label="Phone"
              value={
                contact.phone ||
                ""
              }
              onChange={(e) =>
                updateContact(
                  index,
                  "phone",
                  e.target.value
                )
              }
            />


            <CrudInput
              label="Email"
              value={
                contact.email ||
                ""
              }
              onChange={(e) =>
                updateContact(
                  index,
                  "email",
                  e.target.value
                )
              }
            />


            <label
              className="
                mt-3
                flex
                items-center
                gap-2
                text-sm
                font-medium
              "
            >

              <input
                type="checkbox"
                checked={
                  Boolean(
                    contact.isPrimary
                  )
                }
                onChange={(e) =>
                  updateContact(
                    index,
                    "isPrimary",
                    e.target.checked
                  )
                }
              />

              Primary Contact

            </label>


            <button
              type="button"
              className="
                mt-3
                text-sm
                font-semibold
                text-red-500
                hover:text-red-700
              "
              onClick={() =>
                removeContact(
                  index
                )
              }
            >
              Remove
            </button>

          </div>

        )
      )}


      {/* =========================================================
          STATUS
      ========================================================== */}

      <CrudSelect
        label="Status"
        value={
          form.status
        }
        onChange={(e) =>
          setForm(
            prev => ({
              ...prev,
              status:
                e.target.value,
            })
          )
        }
        options={[
          {
            value: "active",
            label: "Active",
          },
          {
            value: "inactive",
            label: "Inactive",
          },
          {
            value: "prospect",
            label: "Prospect",
          },
          {
            value: "closed",
            label: "Closed",
          },
        ]}
      />

    </CrudModal>

  );
}